import { checkSchema, isActionVisible } from "./checkSchema";
import { isAuth } from "../configPanel/actionSetup";

// Reference actions are stateless option lookups (RFC: reference-data actions);
// they are invoked by form widgets, never configured, so they must not render
// as configuration forms anywhere.
//
// Credentials are exempt from the direction filter: auth is not a direction, so
// a connector that types its auth action `pull` or `push` still has to collect
// it. Without this a creation screen could neither show those fields nor block
// on them, and a Connection could be created with no credentials at all
// (GUNDI-5660 — product call: an integration that cannot authenticate is the one
// thing creation must stop for).
export const isConfigurableAction = (item: any, exclude: "push" | "pull"): boolean =>
  !!item && item.type !== "reference" && (isAuth(item) || item.type !== exclude);

export const filterConfigurableActions = (actions: any[] | undefined | null, exclude: "push" | "pull") =>
  (actions ?? []).filter((item) => isConfigurableAction(item, exclude));

// The configuration sections a creation screen shows — and therefore the
// sections its submit must validate. Kept here so the screen and its validator
// can never disagree about which actions exist (GUNDI-5642 follow-up: required
// auth fields went unenforced because the two lists were computed apart).
export const connectionSetupActions = (actions: any[] | undefined | null) =>
  filterConfigurableActions(actions, "pull").filter((a) => isActionVisible(a) && checkSchema(a));

// A standalone Connection has no direction yet — its role comes from the Routes
// it later joins — so no direction may be filtered out of its setup. Picking a
// technology under "Can send data to Gundi" and then finding no section for the
// pull action that provides that very capability is the failure this avoids.
// Validation is unchanged: only `blockingSetupActions` can stop a save.
export const integrationSetupActions = (actions: any[] | undefined | null) =>
  (actions ?? []).filter((a) =>
    !!a && a.type !== "reference" && isActionVisible(a) && checkSchema(a));

// Pull actions render as sections at creation time too (GUNDI-5660) — the user
// sees the full shape of the integration up front. Only auth blocks the save
// (blockingSetupActions); required fields of pull actions are neither validated
// nor badged here, and can be filled later on the flow map.
export const routeSetupActions = (actions: any[] | undefined | null) =>
  filterConfigurableActions(actions, "push").filter((a) => isActionVisible(a));

// What a creation screen is allowed to BLOCK on: credentials only (product call,
// GUNDI-5642). Without auth the record cannot talk to anything, so its required
// fields are worth stopping for; every other action's required fields are
// configured after the record exists and must not hold up creation.
export const blockingSetupActions = (actions: any[] | undefined | null) =>
  // `isConfigurableAction` keeps reference actions out: they are invoked by form
  // widgets and never rendered as a section, so their required fields belong to
  // nobody's form and must not gate anything.
  (actions ?? []).filter((a) =>
    isAuth(a) && isConfigurableAction(a, "pull") && isActionVisible(a) && checkSchema(a));
